import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Dumbbell, Heart, Stethoscope, User, Trophy, Target } from 'lucide-react';
import { usePlayerData } from './PlayerDataContext';
import { getInjuries, getIllnesses, getSsgPeriods, getSsgPlayerSummary } from '../../api';
import { SSG_COMPETITION_NAME } from '../../ssgConstants';

const TILES = [
  {
    id: 'gps',
    icon: Activity,
    color: '#3b82f6',
    label: 'GPS & Load',
    path: '/player/me/gps',
    available: true,
    subtitle: ({ lastSession }) =>
      lastSession ? `Last: ${Math.round(lastSession.hmld).toLocaleString()}m HMLD` : 'No data yet',
    badge: () => false,
  },
  {
    id: 'technical',
    icon: Target,
    color: '#6a5bd6',
    label: 'Technical data',
    path: '/player/me/technical',
    available: true,
    subtitle: () => 'Last 8 games · KPI breakdown',
    badge: () => false,
  },
  {
    id: 'tests',
    icon: Dumbbell,
    color: '#10b981',
    label: 'Physical Tests',
    path: '/player/me/tests',
    available: true,
    subtitle: ({ playerRecord }) =>
      playerRecord?.id ? 'View test results' : 'Last test: —',
    badge: () => false,
  },
  {
    id: 'wellness',
    icon: Heart,
    color: '#f59e0b',
    label: 'Wellness',
    path: '/player/me/wellness',
    available: true,
    subtitle: ({ todayWellness }) =>
      todayWellness ? 'Today: submitted ✓' : 'Today: not submitted',
    subtitleColor: ({ todayWellness }) =>
      todayWellness ? '#10b981' : '#f59e0b',
    badge: ({ todayWellness }) => !todayWellness,
  },
  {
    id: 'medical',
    icon: Stethoscope,
    color: '#ef4444',
    label: 'Medical',
    path: '/player/me/medical',
    available: true,
    subtitle: ({ activeInjury, activeIllness }) =>
      activeInjury ? `${activeInjury.body_part} — ${activeInjury.current_stage.replace('_', ' ')}` :
      activeIllness ? activeIllness.illness_type :
      'All clear ✓',
    subtitleColor: ({ activeInjury, activeIllness }) =>
      (activeInjury || activeIllness) ? '#ef4444' : '#10b981',
    badge: ({ activeInjury, activeIllness }) => !!(activeInjury || activeIllness),
  },
  {
    id: 'ssg',
    icon: Trophy,
    color: '#f59e0b',
    label: SSG_COMPETITION_NAME,
    path: '/player/me/ssg',
    available: true,
    subtitle: ({ ssgSummary }) =>
      ssgSummary ? `Rank ${ssgSummary.shared_rank ? '=' : ''}${ssgSummary.rank} · ${ssgSummary.total_points} pts` : 'View standings',
    badge: () => false,
  },
  {
    id: 'profile',
    icon: User,
    color: '#8b5cf6',
    label: 'My Profile',
    path: '/player/settings',
    available: true,
    subtitle: ({ playerRecord }) => {
      const p = playerRecord;
      return [p?.position, p?.squad_number ? `#${p.squad_number}` : null].filter(Boolean).join(' · ') || '—';
    },
    badge: () => false,
  },
];

export default function PlayerMe() {
  const navigate   = useNavigate();
  const data       = usePlayerData();
  const { playerRecord } = data;
  const [toast, setToast] = useState(null);
  const [activeInjury,  setActiveInjury]  = useState(null);
  const [activeIllness, setActiveIllness] = useState(null);
  const [ssgSummary, setSsgSummary] = useState(null);

  useEffect(() => {
    if (!playerRecord?.id) return;
    const pid = playerRecord.id;
    Promise.all([getInjuries(pid), getIllnesses(pid)]).then(([inj, ill]) => {
      setActiveInjury(inj.find(i => !i.is_closed) || null);
      setActiveIllness(ill.find(i => i.status !== 'cleared') || null);
    }).catch(() => {});
    getSsgPeriods().then(periods => {
      const active = periods.find(p => p.status === 'active') || periods[0];
      if (!active) return null;
      return getSsgPlayerSummary(pid, active.id);
    }).then(sum => setSsgSummary(sum || null)).catch(() => {});
  }, [playerRecord?.id]);

  const handleTap = tile => {
    if (!tile.available) {
      setToast(tile.label);
      setTimeout(() => setToast(null), 2000);
      return;
    }
    navigate(tile.path);
  };

  const tileData = { ...data, activeInjury, activeIllness, ssgSummary };
  const isOdd = TILES.length % 2 !== 0;

  return (
    <div style={{ padding: '14px 14px 24px' }}>

      {/* Player identity bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, padding: '12px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#fff', flexShrink: 0 }}>
          {(playerRecord?.name || '?').charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{playerRecord?.name || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {[playerRecord?.position, playerRecord?.squad_number ? `#${playerRecord.squad_number}` : null].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#fff', padding: '10px 22px', borderRadius: 99, fontSize: 13, fontWeight: 600, zIndex: 300, boxShadow: '0 4px 20px rgba(0,0,0,0.5)', whiteSpace: 'nowrap' }}>
          {toast} — Coming soon
        </div>
      )}

      {/* 2-column tile grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {TILES.map((tile, idx) => {
          const Icon      = tile.icon;
          const sub       = tile.subtitle(tileData);
          const subColor  = tile.subtitleColor ? tile.subtitleColor(tileData) : 'var(--text-muted)';
          const hasBadge  = tile.badge(tileData);
          const lastAndOdd = isOdd && idx === TILES.length - 1;

          return (
            <div
              key={tile.id}
              onClick={() => handleTap(tile)}
              style={{
                position: 'relative',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 16,
                padding: '20px 12px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 120,
                cursor: 'pointer',
                opacity: tile.available ? 1 : 0.55,
                textAlign: 'center',
                // Center last tile if grid is odd
                gridColumn: lastAndOdd ? '1 / -1' : undefined,
                width:      lastAndOdd ? 'calc(50% - 5px)' : '100%',
                justifySelf: lastAndOdd ? 'center' : undefined,
              }}
            >
              {/* Badge dot */}
              {hasBadge && (
                <div style={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
              )}
              {/* Coming soon label */}
              {!tile.available && (
                <div style={{ position: 'absolute', top: 9, right: 10, fontSize: 8, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Soon</div>
              )}

              <Icon size={32} color={tile.color} strokeWidth={1.5} style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>{tile.label}</div>
              <div style={{ fontSize: 11, color: subColor, lineHeight: 1.3 }}>{sub}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
